import React from 'react';
import { Link } from 'react-router-dom';

// The critical findings a page analysis carries (critical_findings, [{ id, issue, hidden_block_count }] from the
// backend). The page summary shows each as a red CRITICAL sub-heading over its issue and why it costs the page the
// recommendation, in paragraphs of at most two sentences. The site summary shows one CRITICAL sub-heading, then for
// each critical issue its explanation, its bold sentence and the pages with it, each linking to its analysis view.

// Why each critical finding costs the page the recommendation, by its id: the explanation, then the closing sentence
// shown in bold on its own line.
const c_CriticalExplanations = {
	hidden_subject: "Some answer engines download only the HTML, before any scripts run, so they may never see the fully rendered page.",
	blank_page_source: "Answer engines that only download the HTML have no text to quote, no subject to identify and nothing to cite from this page. Engines that run scripts may still see its content, but the page depends on them doing so."
};

const c_CriticalBoldSentences = {
	hidden_subject: "This confusion of meaning on its own may lead an answer engine to recommend another site's page instead.",
	blank_page_source: "Content that only appears once scripts run may be invisible to some answer engines."
};

// The site summary's explanation of each critical issue, by its id, where it differs from the page summary's.
const c_SiteCriticalExplanations = {
	hidden_subject: "These pages carry text in their HTML that visitors never see, and that text introduces subjects the visible pages do not cover. Some answer engines fetch just a page's HTML and skip its scripts, so the rendered page may never reach them. Such an engine may take those hidden subjects as part of each page below.",
	blank_page_source: "Some AI crawlers read a page without running scripts, so they find no content on these pages. For those engines, each one offers nothing to quote, no subject to recognise and nothing to cite. Engines that run scripts may still see the content, but these pages rely on that."
};

// The critical issues the site summary lists the pages of, in order, each with the sentence shown in bold after its
// explanation.
const c_SiteCriticalIssues = [
	{ id: 'hidden_subject', bold_sentence: "Each one of these mismatches on its own may lead an answer engine to recommend another site's page instead." },
	{ id: 'blank_page_source', bold_sentence: "Content that only appears once scripts run may be invisible to some answer engines." }
];

const is_bold_sentence = (sentence)=>{
	return Object.values(c_CriticalBoldSentences).includes(sentence);
};

// A text as paragraphs of at most two sentences each; a sentence of c_CriticalBoldSentences always stands as its
// own paragraph. Every summary paragraph of the report is split this way.
export const split_into_paragraphs = (text)=>{
	const sentences = String(text).split(/(?<=[.!?])\s+(?=[A-Z"'])/).filter((sentence)=>{
		return sentence.trim().length > 0;
	});
	const paragraphs = [];
	let pending = [];
	sentences.forEach((sentence)=>{
		if(is_bold_sentence(sentence)){
			if(pending.length > 0){
				paragraphs.push(pending.join(" "));
				pending = [];
			}

			paragraphs.push(sentence);
			return;
		}

		pending.push(sentence);
		if(pending.length === 2){
			paragraphs.push(pending.join(" "));
			pending = [];
		}
	});

	if(pending.length > 0){
		paragraphs.push(pending.join(" "));
	}

	return paragraphs;
};

// A critical paragraph, in bold when it is one of c_CriticalBoldSentences.
const render_critical_text = (paragraph)=>{
	if(!is_bold_sentence(paragraph)){
		return paragraph;
	}

	return (
		<strong>{paragraph}</strong>
	);
};

const strip_url = (url)=>{
	return String(url).replace('https://', '').replace('http://', '').replace(/\/+$/, '');
};

// One critical finding of a page: its issue, its explanation and its bold closing sentence.
const render_critical_finding = (critical_finding, key)=>{
	const bold_sentence = c_CriticalBoldSentences[critical_finding.id];
	if(!bold_sentence){
		return null;
	}

	const issue_text = critical_finding.issue ? critical_finding.issue + " " : "";

	return (
		<React.Fragment key={key}>
			<h4 className="stage-summary-heading critical-summary-heading">CRITICAL</h4>
			{split_into_paragraphs(issue_text + c_CriticalExplanations[critical_finding.id] + " " + bold_sentence).map((paragraph, paragraph_index)=>(
				<p className="stage-summary-paragraph" key={paragraph_index}>{render_critical_text(paragraph)}</p>
			))}
		</React.Fragment>
	);
};

// Every critical finding of a page, or null when it has none.
export const render_critical_findings = (critical_findings)=>{
	if(!Array.isArray(critical_findings) || (critical_findings.length === 0)){
		return null;
	}

	return critical_findings.map((critical_finding, finding_index)=>{
		return render_critical_finding(critical_finding, finding_index);
	});
};

// A page's critical finding with the id, or null.
const get_critical_finding = (page_analysis, critical_finding_id)=>{
	const findings = Array.isArray(page_analysis.critical_findings) ? page_analysis.critical_findings : [];
	const finding = findings.find((candidate)=>{
		return candidate.id === critical_finding_id;
	});
	return finding ? finding : null;
};

// A page's line under a critical issue: its url, followed by its hidden block count for a hidden subject.
const get_critical_page_line = (page_analysis, critical_finding_id)=>{
	const finding = get_critical_finding(page_analysis, critical_finding_id);
	if(critical_finding_id !== 'hidden_subject'){
		return strip_url(page_analysis.url);
	}

	return strip_url(page_analysis.url) + ' - ' + finding.hidden_block_count + ((finding.hidden_block_count === 1) ? ' block' : ' blocks');
};

// One critical issue of a site: its explanation, its bold sentence, then one bold line per page with it, linking to
// the page's analysis view. The CRITICAL heading goes over the first issue only.
const render_site_critical_issue = (issue, issue_pages, is_first)=>{
	return (
		<React.Fragment key={issue.id}>
			{is_first ? <h4 className="stage-summary-heading critical-summary-heading">CRITICAL</h4> : null}
			{split_into_paragraphs(c_SiteCriticalExplanations[issue.id]).map((paragraph, paragraph_index)=>(
				<p className="stage-summary-paragraph" key={paragraph_index}>{paragraph}</p>
			))}
			<p className="stage-summary-paragraph"><strong>{issue.bold_sentence}</strong></p>
			{issue_pages.map((page_analysis)=>(
				<p className="stage-summary-paragraph" key={page_analysis.id}>
					<strong>
						<Link to={'/site-page/' + page_analysis.site_page_id} className="summary-page-link">
							{get_critical_page_line(page_analysis, issue.id)}
						</Link>
					</strong>
				</p>
			))}
		</React.Fragment>
	);
};

// The critical findings of a site's pages (page_analyses_scoring rows), as c_SiteCriticalIssues orders them, or null
// when no page has one.
export const render_site_critical_findings = (page_analyses)=>{
	const pages = Array.isArray(page_analyses) ? page_analyses : [];
	const rendered = [];
	c_SiteCriticalIssues.forEach((issue)=>{
		const issue_pages = pages.filter((page_analysis)=>{
			return get_critical_finding(page_analysis, issue.id) !== null;
		});
		if(issue_pages.length > 0){
			rendered.push(render_site_critical_issue(issue, issue_pages, rendered.length === 0));
		}
	});

	if(rendered.length === 0){
		return null;
	}

	return rendered;
};
